import { X } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { animationFrameId } from "@/document/animation/applyAnimation";
import { findSceneNode } from "@/document/model/document";
import type { CurvaDocument } from "@/document/model/document";
import type { AnimationGraph } from "@/document/model/animation";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import type { FrameNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";
import { Renderer } from "@/renderer/Renderer";
import { renderAnimationPreviewToCanvas } from "./animationPreviewRender";
import { resizeObserverBorderBoxSize } from "./resizeObserverGeometry";

interface AnimationPreviewWindowProps {
  animationGraph: AnimationGraph;
  activeAnimationRootId?: string;
  animationFrames: FrameNode[];
  timeMs: number;
  onClose: () => void;
}

interface PanelGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

const storageKey = "curva:animation-preview-window";
const viewportMargin = 12;
const minPanelWidth = 260;
const minPanelHeight = 180;

export function AnimationPreviewWindow({
  animationGraph,
  activeAnimationRootId,
  animationFrames,
  timeMs,
  onClose,
}: AnimationPreviewWindowProps) {
  const document = useStoreSubscribe(documentStore.document);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previewAreaRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const renderGenerationRef = useRef(0);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; panelX: number; panelY: number } | undefined>(undefined);
  const [geometry, setGeometry] = useState<PanelGeometry>(() => initialPanelGeometry());
  const [previewSize, setPreviewSize] = useState({ width: 320, height: 200 });

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const borderBox = resizeObserverBorderBoxSize(entry);
      const width = Math.max(minPanelWidth, borderBox.width);
      const height = Math.max(minPanelHeight, borderBox.height);
      setGeometry(current => {
        if (Math.abs(current.width - width) < 0.5 && Math.abs(current.height - height) < 0.5) {
          return current;
        }
        const next = clampPanelGeometry({ ...current, width, height });
        storePanelGeometry(next);
        return next;
      });
    });

    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const area = previewAreaRef.current;
    if (!area) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      setPreviewSize({
        width: Math.max(1, entry.contentRect.width),
        height: Math.max(1, entry.contentRect.height),
      });
    });

    observer.observe(area);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setGeometry(current => {
        const next = clampPanelGeometry(current);
        storePanelGeometry(next);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const generation = renderGenerationRef.current + 1;
    renderGenerationRef.current = generation;
    const renderer = rendererRef.current ?? new Renderer(canvas, { alpha: false, preserveDrawingBuffer: true });
    rendererRef.current = renderer;

    const scale = previewScaleForFrame(document, animationGraph, activeAnimationRootId, animationFrames, previewSize);
    void renderAnimationPreviewToCanvas(renderer, {
      document,
      animationGraph,
      activeAnimationRootId,
      animationFrames,
      timeMs,
      scale,
    }).then(() => {
      if (renderGenerationRef.current !== generation) return;
    }).catch(error => {
      if (renderGenerationRef.current === generation) {
        console.warn("Failed to render animation preview window", error);
      }
    });
  }, [activeAnimationRootId, animationFrames, animationGraph, document, previewSize, timeMs]);

  useEffect(() => {
    return () => {
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
  }, []);

  function handleHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panelX: geometry.x,
      panelY: geometry.y,
    };
  }

  function handleHeaderPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = clampPanelGeometry({
      ...geometry,
      x: drag.panelX + event.clientX - drag.startX,
      y: drag.panelY + event.clientY - drag.startY,
    });
    setGeometry(next);
  }

  function handleHeaderPointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = undefined;
    storePanelGeometry(geometry);
  }

  return (
    <section
      ref={panelRef}
      className="fixed z-[1000] flex min-h-[180px] min-w-[260px] resize overflow-hidden rounded-md border border-white/10 bg-[#171b20] shadow-2xl shadow-black/45"
      style={{ left: geometry.x, top: geometry.y, width: geometry.width, height: geometry.height }}
      aria-label="Animation preview window"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          className="flex h-8 shrink-0 cursor-move items-center justify-between border-b border-white/10 bg-[#20262d] px-2 text-[12px] font-medium text-slate-300"
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
        >
          <span>Animation preview</span>
          <button
            type="button"
            className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
            aria-label="Hide animation preview window"
            onPointerDown={event => event.stopPropagation()}
            onClick={onClose}
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div ref={previewAreaRef} className="flex min-h-0 flex-1 items-center justify-center bg-[#0f1318] p-2">
          <canvas className="block max-h-full max-w-full" ref={canvasRef} />
        </div>
      </div>
    </section>
  );
}

function previewScaleForFrame(
  document: CurvaDocument,
  animationGraph: AnimationGraph,
  activeAnimationRootId: string | undefined,
  animationFrames: FrameNode[],
  previewSize: { width: number; height: number },
): number {
  const frameId = animationFrameId(animationGraph, activeAnimationRootId) ?? animationFrames[0]?.id;
  const frame = frameId ? findSceneNode(document, frameId) : undefined;
  const bounds: Rect = frame?.type === "frame" ? frame.bounds : { x: 0, y: 0, width: 1, height: 1 };
  return Math.max(1, Math.min(previewSize.width / Math.max(1, bounds.width), previewSize.height / Math.max(1, bounds.height)) * window.devicePixelRatio);
}

function initialPanelGeometry(): PanelGeometry {
  const stored = readStoredPanelGeometry();
  if (stored) {
    return clampPanelGeometry(stored);
  }
  const width = 360;
  const height = 260;
  return clampPanelGeometry({
    x: Math.max(viewportMargin, window.innerWidth - width - 320),
    y: 72,
    width,
    height,
  });
}

function readStoredPanelGeometry(): PanelGeometry | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PanelGeometry>;
    if (
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      typeof parsed.width === "number" &&
      typeof parsed.height === "number"
    ) {
      return parsed as PanelGeometry;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function storePanelGeometry(geometry: PanelGeometry): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(geometry));
  } catch {
    // Ignore storage failures; the window still works for this session.
  }
}

function clampPanelGeometry(geometry: PanelGeometry): PanelGeometry {
  const width = Math.min(Math.max(minPanelWidth, geometry.width), Math.max(minPanelWidth, window.innerWidth - viewportMargin * 2));
  const height = Math.min(Math.max(minPanelHeight, geometry.height), Math.max(minPanelHeight, window.innerHeight - viewportMargin * 2));
  const maxX = Math.max(viewportMargin, window.innerWidth - width - viewportMargin);
  const maxY = Math.max(viewportMargin, window.innerHeight - height - viewportMargin);
  return {
    x: Math.min(Math.max(viewportMargin, geometry.x), maxX),
    y: Math.min(Math.max(viewportMargin, geometry.y), maxY),
    width,
    height,
  };
}
